import { useCallback, useEffect, useRef, useState } from "react";
import { clientStore } from "../lib/clientStorage";
import { ACTIVE_PERSISTENCE_USER_KEY, getActivePersistenceUser, getPersistenceGeneration, hasPersistenceInvalidationFence, persistenceWriteAllowed, PERSISTENCE_LOCAL_CLEAR_KEY, PERSISTENCE_LOGOUT_KEY, readPersistenceEvent } from "../lib/persistenceIdentity";
import {
  persistedStatePolicy,
  type PersistedStateKey,
  valueFitsPolicy,
} from "../lib/persistenceRegistry";
import {
  markPersistenceMutation,
  registerPersistenceFlushParticipant,
  type PersistenceFlushReceipt,
} from "../lib/persistenceReloadBarrier";

const NAMESPACE = "persist";
const DEBOUNCE_MS = 400;
const acceptAny = () => true;

interface PersistedStateOptions<T> {
  serialize?: (v: T) => string;
  deserialize?: (raw: string) => T;
  migrate?: (rawValue: string, fromVersion: number) => T | null;
  validate?: (value: T) => boolean;
}

export interface PersistedEnvelope {
  version: number;
  timestamp: number;
  value: string;
  generation?: number;
  ownerGeneration?: number;
}
export function serializePersistedEnvelope<T>(
  value: T, version: number, timestamp: number, generation: number,
  ownerGeneration: number,
  serialize: (value: T) => string
): string {
  return JSON.stringify({
    version,
    timestamp,
    generation,
    ownerGeneration,
    value: serialize(value),
  });
}

export function readPersistedEnvelope<T>(
  raw: string | null,
  version: number,
  ttlMs: number,
  deserialize: (value: string) => T,
  now = Date.now(),
  migrate?: (rawValue: string, fromVersion: number) => T | null,
  validate: (value: T) => boolean = acceptAny,
  expectedGeneration?: number,
  expectedOwnerGeneration?: number,
  allowOwnerScopedV1Migration = false
): { value: T; timestamp: number; migrated: boolean } | null {
  if (!raw) return null;
  try {
    const env = JSON.parse(raw) as PersistedEnvelope;
    const ownerScopedV1 = allowOwnerScopedV1Migration &&
      version === 2 && env.version === 1 && env.ownerGeneration === undefined;
    if (typeof env.value !== "string" || !Number.isInteger(env.version) || !Number.isFinite(env.timestamp) ||
        env.timestamp > now + 5 * 60 * 1000 || now - env.timestamp >= ttlMs ||
        (expectedGeneration !== undefined && env.generation !== expectedGeneration) ||
        (expectedOwnerGeneration !== undefined &&
          env.ownerGeneration !== expectedOwnerGeneration && !ownerScopedV1)) return null;
    if (env.version !== version) {
      const migrated = migrate?.(env.value, env.version) ??
        (ownerScopedV1 ? deserialize(env.value) : null);
      return migrated === undefined || migrated === null || !validate(migrated)
        ? null : { value: migrated, timestamp: now, migrated: true };
    }
    const value = deserialize(env.value);
    return validate(value) ? { value, timestamp: env.timestamp, migrated: false } : null;
  } catch {
    return null;
  }
}

function canMigrateOwnerScopedV1(userId: string | null, generation: number): boolean {
  return Boolean(userId) &&
    !hasPersistenceInvalidationFence(userId as string, generation);
}

function buildKey(
  logicalKey: string,
  userId: string | null
): string {
  return `${NAMESPACE}:${userId ?? "anon"}:${logicalKey}`;
}

/**
 * Persisted UI state with TTL, version gating, and flush-on-hide.
 *
 * Backed by `clientStore` (localStorage) so it survives tab-switch, page reload,
 * AND a mobile OS discarding the backgrounded tab — the case plain sessionStorage
 * lost. Stale or wrong-version entries fall back to defaultValue; expired entries
 * are pruned on read.
 *
 * Writes are debounced 400ms. On `visibilitychange: hidden` or `pagehide` the
 * latest value is flushed synchronously so fast app backgrounding doesn't drop
 * the last edit.
 */
export function usePersistedState<T>(
  key: PersistedStateKey,
  defaultValue: T,
  options: PersistedStateOptions<T> = {}
): [T, (v: T | ((prev: T) => T)) => void] {
  const policy = persistedStatePolicy(key);
  const {
    serialize = JSON.stringify as (v: T) => string,
    deserialize = JSON.parse as (raw: string) => T,
    migrate,
    validate = acceptAny,
  } = options;
  const version = policy.version;
  const ttlMs = policy.ttlMs;
  const canonicalDefaultValue = policy.defaultValue as T;
  const validateRegistered = useCallback(
    (value: T) => policy.validate?.(value) !== false && validate(value),
    [policy, validate]
  );

  const userIdRef = useRef<string | null>(null);
  const pendingRef = useRef<{ storageKey: string; envelope: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timestampRef = useRef(0);
  const invalidatedRef = useRef(false);
  const generationRef = useRef(0);

  const [state, setState] = useState<T>(() => {
    try {
      const uid = getActivePersistenceUser();
      userIdRef.current = uid;
      generationRef.current = getPersistenceGeneration(uid);
      const storageKey = buildKey(key, uid);
      const raw = clientStore.getItem(storageKey);
      if (!raw) return defaultValue;
      if (!valueFitsPolicy(raw, policy)) {
        clientStore.removeItem(storageKey);
        return defaultValue;
      }
      const keyGeneration = clientStore.generation(storageKey);
      const restored = readPersistedEnvelope(
        raw, version, ttlMs, deserialize, Date.now(), migrate,
        validateRegistered, keyGeneration, generationRef.current,
        canMigrateOwnerScopedV1(uid, generationRef.current)
      );
      if (!restored) {
        clientStore.removeItem(storageKey);
        return defaultValue;
      }
      timestampRef.current = restored.timestamp;
      if (restored.migrated) {
        clientStore.setItemGuarded(storageKey, serializePersistedEnvelope(
          restored.value, version, restored.timestamp, keyGeneration,
          generationRef.current, serialize
        ), keyGeneration);
      }
      return restored.value;
    } catch {
      return defaultValue;
    }
  });

  const commitPending = useCallback((
    pending: { storageKey: string; envelope: string }
  ): boolean => {
    const env = JSON.parse(pending.envelope) as PersistedEnvelope;
    if (clientStore.setItemGuarded(
      pending.storageKey,
      pending.envelope,
      env.generation ?? 0
    )) return true;
    const tombstoneAt = clientStore.tombstoneTimestamp(pending.storageKey);
    if (
      !Number.isFinite(tombstoneAt) ||
      env.timestamp <= tombstoneAt ||
      !persistenceWriteAllowed(userIdRef.current, generationRef.current)
    ) return false;
    const nextGeneration = clientStore.generation(pending.storageKey);
    if (!Number.isSafeInteger(nextGeneration) || nextGeneration < 0) return false;
    const rebased = JSON.stringify({ ...env, generation: nextGeneration });
    pending.envelope = rebased;
    return clientStore.setItemGuarded(
      pending.storageKey,
      rebased,
      nextGeneration
    );
  }, []);

  const flushPending = useCallback(
    (): PersistenceFlushReceipt => {
      const participant = `persist:${key}`;
      try {
        if (invalidatedRef.current || !persistenceWriteAllowed(userIdRef.current, generationRef.current)) {
          return {
            participant,
            dirty: pendingRef.current !== null,
            durable: pendingRef.current === null,
            reason: "identity-invalidated",
          };
        }
        const pending = pendingRef.current;
        if (!pending) return { participant, dirty: false, durable: true };
        if (!commitPending(pending)) {
          return {
            participant,
            dirty: true,
            durable: false,
            reason: "guarded-write-failed",
          };
        }
        const verified = clientStore.getDurableItem(pending.storageKey) === pending.envelope;
        if (verified && pendingRef.current === pending) {
          timestampRef.current = (JSON.parse(pending.envelope) as PersistedEnvelope).timestamp;
          pendingRef.current = null;
        }
        return {
          participant,
          dirty: true,
          durable: verified,
          reason: verified ? undefined : "durable-readback-mismatch",
        };
      } catch {
        return {
          participant,
          dirty: pendingRef.current !== null,
          durable: false,
          reason: "flush-threw",
        };
      }
    },
    [commitPending, key]
  );

  const writeNow = useCallback(() => {
    flushPending();
  }, [flushPending]);

  const setPersisted = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (p: T) => T)(prev)
            : updater;
        const storageKey = buildKey(key, userIdRef.current);
        try {
          if (!validateRegistered(next)) {
            pendingRef.current = null;
            clientStore.reportMemoryOnly("invalid", storageKey);
            return next;
          }
          const generation = clientStore.generation(storageKey);
          const envelope = serializePersistedEnvelope(
            next, version, Date.now(), generation,
            generationRef.current, serialize
          );
          if (!valueFitsPolicy(envelope, policy)) {
            pendingRef.current = null;
            clientStore.reportMemoryOnly("oversize", storageKey);
          } else {
            pendingRef.current = { storageKey, envelope };
            markPersistenceMutation();
          }
        } catch {
          pendingRef.current = null;
          clientStore.reportMemoryOnly("invalid", storageKey);
        }
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(writeNow, DEBOUNCE_MS);
        return next;
      });
    },
    [key, version, serialize, writeNow, policy, validateRegistered]
  );

  useEffect(() => {
    const flush = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      flushPending();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flushPending]);

  useEffect(
    () => registerPersistenceFlushParticipant(`persist:${key}`, flushPending),
    [flushPending, key]
  );

  useEffect(() => {
    invalidatedRef.current = false;
    return clientStore.subscribe((changedKey, raw) => {
      if (changedKey === ACTIVE_PERSISTENCE_USER_KEY) {
        const uid = raw || null;
        const previousUid = userIdRef.current;
        if (uid === userIdRef.current && getPersistenceGeneration(uid) === generationRef.current) return;
        userIdRef.current = uid;
        generationRef.current = getPersistenceGeneration(uid);
        invalidatedRef.current = !uid;
        pendingRef.current = null;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        if (!uid) { setState(defaultValue); return; }
        if (uid === previousUid && policy.retainVisibleStateAfterClear) {
          const clearEvent = readPersistenceEvent(
            clientStore.getItem(PERSISTENCE_LOCAL_CLEAR_KEY),
            "local-clear"
          );
          if (
            clearEvent?.userId === uid &&
            clearEvent.generation === generationRef.current
          ) {
            // Keep the Data & Privacy surface mounted long enough to show
            // its exact receipt. The value is no longer stored and is not
            // auto-written; reload still returns to the registered default.
            timestampRef.current = 0;
            return;
          }
        }
        if (uid === previousUid) {
          const clearEvent = readPersistenceEvent(
            clientStore.getItem(PERSISTENCE_LOCAL_CLEAR_KEY),
            "local-clear"
          );
          if (
            clearEvent?.userId === uid &&
            clearEvent.generation === generationRef.current
          ) {
            timestampRef.current = 0;
            setState(canonicalDefaultValue);
            return;
          }
        }
        const reboundKey = buildKey(key, uid);
        const reboundRaw = clientStore.getItem(reboundKey);
        if (reboundRaw && !valueFitsPolicy(reboundRaw, policy)) {
          clientStore.removeItem(reboundKey);
        }
        const rebound = readPersistedEnvelope(
          reboundRaw && valueFitsPolicy(reboundRaw, policy) ? reboundRaw : null,
          version, ttlMs, deserialize, Date.now(), migrate, validateRegistered,
          clientStore.generation(reboundKey), generationRef.current,
          canMigrateOwnerScopedV1(uid, generationRef.current)
        );
        timestampRef.current = rebound?.timestamp ?? 0;
        setState(rebound?.value ?? defaultValue);
        return;
      }
      if (changedKey === PERSISTENCE_LOCAL_CLEAR_KEY && raw) {
        const clearEvent = readPersistenceEvent(raw, "local-clear");
        if (clearEvent?.userId === userIdRef.current) {
          generationRef.current = clearEvent.generation;
          invalidatedRef.current = false;
          pendingRef.current = null;
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
          if (!policy.retainVisibleStateAfterClear) {
            setState(canonicalDefaultValue);
          }
        }
        return;
      }
      if (changedKey === PERSISTENCE_LOGOUT_KEY && raw) {
        const event = readPersistenceEvent(raw, "logout");
        if (event && (!event.userId || event.userId === userIdRef.current)) {
          invalidatedRef.current = true;
          pendingRef.current = null;
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
          setState(canonicalDefaultValue);
        }
        return;
      }
      const storageKey = buildKey(key, userIdRef.current);
      if (changedKey !== storageKey) return;
      if (raw === null) {
        timestampRef.current = Date.now();
        const pending = pendingRef.current;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        const clearEvent = readPersistenceEvent(
          clientStore.getItem(PERSISTENCE_LOCAL_CLEAR_KEY),
          "local-clear"
        );
        if (
          clearEvent?.userId === userIdRef.current &&
          clearEvent.generation === generationRef.current
        ) {
          if (!policy.retainVisibleStateAfterClear) {
            setState(canonicalDefaultValue);
          }
          return;
        }
        if (pending) {
          try {
            const env = JSON.parse(pending.envelope) as PersistedEnvelope;
            const tombstoneAt = clientStore.tombstoneTimestamp(storageKey);
            const nextGeneration = clientStore.generation(storageKey);
            if (
              Number.isFinite(tombstoneAt) &&
              env.timestamp > tombstoneAt &&
              Number.isSafeInteger(nextGeneration) && nextGeneration >= 0 &&
              persistenceWriteAllowed(userIdRef.current, generationRef.current)
            ) {
              pendingRef.current = {
                storageKey,
                envelope: JSON.stringify({ ...env, generation: nextGeneration }),
              };
              saveTimerRef.current = setTimeout(writeNow, 0);
              return;
            }
          } catch {
            /* malformed pending state falls through to deletion-wins */
          }
        }
        pendingRef.current = null;
        setState(defaultValue);
        return;
      }
      if (!valueFitsPolicy(raw, policy)) {
        clientStore.removeItem(storageKey);
        setState(defaultValue);
        return;
      }
      const incoming = readPersistedEnvelope(raw, version, ttlMs, deserialize, Date.now(), migrate, validateRegistered,
        clientStore.generation(storageKey), generationRef.current,
        canMigrateOwnerScopedV1(userIdRef.current, generationRef.current));
      let pendingTimestamp = 0;
      try {
        pendingTimestamp = pendingRef.current
          ? (JSON.parse(pendingRef.current.envelope) as PersistedEnvelope).timestamp
          : 0;
      } catch {
        pendingTimestamp = 0;
      }
      if (
        !incoming ||
        incoming.timestamp <= Math.max(timestampRef.current, pendingTimestamp)
      ) return;
      timestampRef.current = incoming.timestamp;
      pendingRef.current = null;
      setState(incoming.value);
    });
  }, [key, version, ttlMs, deserialize, defaultValue, canonicalDefaultValue, migrate, validateRegistered, policy, writeNow]);

  return [state, setPersisted];
}

/**
 * Clear every persist:* key for the given user (plus the "global" bucket).
 * Called on logout.
 */
export function clearPersistedNamespace(userId: string | null): void {
  const userPrefix = `${NAMESPACE}:${userId ?? "anon"}:`;
  clientStore.removeWhere((k) => k.startsWith(userPrefix));
}
